import { useState } from "react";
import { toast } from "react-hot-toast";
import { usePostB2BContactLeadsMutation } from "../store/generated.ts";

const initialForm = {
  full_name: "",
  company_name: "",
  role: "",
  phone_number: "",
  email: "",
};

const B2BContactLeadForm = ({ buttonText = "Get Started", className = "" }) => {
  const [formData, setFormData] = useState(initialForm);
  const [fieldErrors, setFieldErrors] = useState({});

  const [postB2BContactLead, { isLoading }] = usePostB2BContactLeadsMutation();

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (fieldErrors[name]) {
      setFieldErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }

    if (fieldErrors.non_field_errors) {
      setFieldErrors((prev) => ({
        ...prev,
        non_field_errors: "",
      }));
    }
  };

  const containsNumber = (value) => /\d/.test(value);

  const isValidUSPhoneNumber = (phoneNumber) => {
    const cleaned = phoneNumber.replace(/\D/g, "");

    if (cleaned.length === 10) {
      return true;
    }

    if (cleaned.length === 11 && cleaned.startsWith("1")) {
      return true;
    }

    return false;
  };

  const normalizeUSPhoneNumber = (phoneNumber) => {
    const cleaned = phoneNumber.replace(/\D/g, "");

    if (cleaned.length === 11 && cleaned.startsWith("1")) {
      return `+${cleaned}`;
    }
    return cleaned;
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.full_name.trim()) {
      errors.full_name = "Full name is required.";
    } else if (containsNumber(formData.full_name.trim())) {
      errors.full_name = "Full name cannot contain numbers.";
    }

    if (!formData.company_name.trim()) {
      errors.company_name = "Company name is required.";
    }

    if (formData.role.trim() && containsNumber(formData.role.trim())) {
      errors.role = "Role cannot contain numbers.";
    }

    if (!formData.phone_number.trim()) {
      errors.phone_number = "Phone number is required.";
    } else if (!isValidUSPhoneNumber(formData.phone_number.trim())) {
      errors.phone_number =
        "Please enter a valid US phone number.";
    }

    if (!formData.email.trim()) {
      errors.email = "Email is required.";
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email.trim())) {
        errors.email = "Please enter a valid email address.";
      }
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix the highlighted form errors.");
      return;
    }

    try {
      await postB2BContactLead({
        b2BContactLeadCreate: {
          full_name: formData.full_name.trim(),
          company_name: formData.company_name.trim(),
          role: formData.role.trim(),
          phone_number: normalizeUSPhoneNumber(formData.phone_number.trim()),
          email: formData.email.trim().toLowerCase(),
        },
      }).unwrap();

      toast.success("Thank you! Your details have been submitted successfully. We will contact you soon.");
      setFormData(initialForm);
      setFieldErrors({});
    } catch (error) {
      const apiErrors = error?.data;

      if (apiErrors && typeof apiErrors === "object") {
        const mappedErrors = {};

        Object.keys(apiErrors).forEach((key) => {
          const value = apiErrors[key];
          mappedErrors[key] = Array.isArray(value) ? value[0] : value;
        });

        setFieldErrors(mappedErrors);
        toast.error("Please review the form and try again.");
      } else {
        setFieldErrors({
          non_field_errors: "Something went wrong. Please try again.",
        });
        toast.error("Something went wrong. Please try again.");
      }
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className={`mt-5 w-full max-w-[558px] space-y-4 ${className}`}
    >
      <div>
        <input
          name="full_name"
          value={formData.full_name}
          onChange={handleChange}
          className="w-full h-[40px] rounded-[8px] border border-[#94918E] px-3 text-[13px] placeholder:text-[#9A9A9A] focus:outline-none focus:ring-2 focus:ring-black/10"
          placeholder="Full Name"
        />
        {fieldErrors.full_name ? (
          <p className="mt-1 text-[12px] text-red-600">
            {fieldErrors.full_name}
          </p>
        ) : null}
      </div>

      <div>
        <input
          name="company_name"
          value={formData.company_name}
          onChange={handleChange}
          className="w-full h-[40px] rounded-[8px] border border-[#94918E] px-3 text-[13px] placeholder:text-[#9A9A9A] focus:outline-none focus:ring-2 focus:ring-black/10"
          placeholder="Company Name / Brand Name"
        />
        {fieldErrors.company_name ? (
          <p className="mt-1 text-[12px] text-red-600">
            {fieldErrors.company_name}
          </p>
        ) : null}
      </div>

      <div>
        <input
          name="role"
          value={formData.role}
          onChange={handleChange}
          className="w-full h-[40px] rounded-[8px] border border-[#94918E] px-3 text-[13px] placeholder:text-[#9A9A9A] focus:outline-none focus:ring-2 focus:ring-black/10"
          placeholder="Role"
        />
        {fieldErrors.role ? (
          <p className="mt-1 text-[12px] text-red-600">{fieldErrors.role}</p>
        ) : null}
      </div>

      <div>
        <input
          name="phone_number"
          value={formData.phone_number}
          onChange={handleChange}
          className="w-full h-[40px] rounded-[8px] border border-[#94918E] px-3 text-[13px] placeholder:text-[#9A9A9A] focus:outline-none focus:ring-2 focus:ring-black/10"
          placeholder="Phone Number"
        />
        {fieldErrors.phone_number ? (
          <p className="mt-1 text-[12px] text-red-600">
            {fieldErrors.phone_number}
          </p>
        ) : null}
      </div>

      <div>
        <input
          name="email"
          type="email"
          value={formData.email}
          onChange={handleChange}
          className="w-full h-[40px] rounded-[8px] border border-[#94918E] px-3 text-[13px] placeholder:text-[#9A9A9A] focus:outline-none focus:ring-2 focus:ring-black/10"
          placeholder="Email"
        />
        {fieldErrors.email ? (
          <p className="mt-1 text-[12px] text-red-600">{fieldErrors.email}</p>
        ) : null}
      </div>

      {fieldErrors.non_field_errors ? (
        <p className="text-[12px] text-red-600">
          {fieldErrors.non_field_errors}
        </p>
      ) : null}

      <button
        type="submit"
        disabled={isLoading}
        className="w-[150px] h-[40px] rounded-[8px] bg-gradient-to-r from-[#E2D8D3] via-[#D7A09B] to-[#D1C6C0] text-white text-[13px] leading-none font-normal flex items-center justify-center shadow-[0_18px_45px_rgba(0,0,0,0.10)] transition-transform duration-200 hover:scale-[1.01] active:scale-[0.99] disabled:opacity-70 disabled:cursor-not-allowed"
      >
        {isLoading ? "Submitting..." : buttonText}
      </button>
    </form>
  );
};

export default B2BContactLeadForm;
